import React, { useEffect, useState, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import api, { ROLE_LABELS } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Upload, Download, FileSpreadsheet, ChevronRight, ChevronDown, Users as UsersIcon, UserCircle, Crown, Shield, Plus, Power, RotateCcw, Mail } from "lucide-react";

import { TEAM_ROLES } from "./constants";

export function flattenTree(nodes, depth, expanded, acc) {
  for (let i = 0; i < nodes.length; i++) {
    const n = nodes[i];
    acc.push({ node: n, depth });
    if (expanded[n.user_id] !== false && (n.children || []).length > 0) {
      flattenTree(n.children, depth + 1, expanded, acc);
    }
  }
  return acc;
}

export function HierarchyView({ tree, isAdmin, allUsers, reload, toggleActive, resetToDefault }) {
  const [expanded, setExpanded] = useState({});

  if (!tree || !tree.tree || tree.tree.length === 0) {
    return <div className="brut-card p-10 text-center text-zinc-400 font-mono text-xs">NO HIERARCHY YET — ASSIGN A MANAGER ON THE TABLE VIEW.</div>;
  }

  const flat = flattenTree(tree.tree, 0, expanded, []);

  const toggle = (uid) => setExpanded((p) => ({ ...p, [uid]: p[uid] === false ? true : false }));

  const setManager = async (uid, newMgr) => {
    try {
      await api.patch("/users/" + uid, { manager_id: newMgr || null });
      toast.success("Manager updated");
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };
  const setTeamRole = async (uid, tr) => {
    try {
      await api.patch("/users/" + uid, { team_role: tr });
      toast.success("Team role updated");
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  // Full user for a hierarchy node — we need the whole record (email, is_active, name)
  // to power the inline suspend/reset/delete buttons the admin needs.
  const fullUser = (uid) => allUsers.find((u) => u.user_id === uid);

  const editName = async (u) => {
    const next = prompt(`Edit display name for ${u.name} (${u.email})`, u.name || "");
    if (next === null) return;
    const clean = next.trim();
    if (!clean) { toast.error("Name cannot be empty"); return; }
    try {
      await api.patch("/users/" + u.user_id, { name: clean });
      toast.success("Name updated");
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update name");
    }
  };

  const editEmail = async (u) => {
    const next = prompt(`Update login email for ${u.name}\n\nCurrent: ${u.email}\n\nThey will use the NEW email to sign in.`, u.email || "");
    if (next === null) return;
    const clean = next.trim().toLowerCase();
    if (!clean || !clean.includes("@")) { toast.error("Enter a valid email"); return; }
    if (clean === (u.email || "").toLowerCase()) return;
    try {
      await api.patch("/users/" + u.user_id, { email: clean });
      toast.success(`Email updated to ${clean}`);
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update email");
    }
  };

  const hardDelete = async (u) => {
    const confirmation = prompt(
      `HARD DELETE ${u.name} (${u.email})?\n\n` +
      "This irreversibly removes the user, unassigns their jobs, and clears them as a manager.\n\n" +
      "Type the email to confirm:"
    );
    if (confirmation !== u.email) {
      if (confirmation !== null) toast.error("Confirmation did not match — nothing deleted.");
      return;
    }
    try {
      const { data } = await api.delete(`/admin/users/${u.user_id}/hard-delete`);
      toast.success(`Deleted ${data.deleted_email} · ${data.jobs_reassigned} jobs unassigned · ${data.managers_unset} managers cleared`);
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  const iconFor = (tr) => {
    if (tr === "admin") return <Shield className="w-4 h-4 text-red-700" />;
    if (tr === "manager") return <Crown className="w-4 h-4 text-orange-700" />;
    if (tr === "lead") return <UsersIcon className="w-4 h-4 text-blue-700" />;
    return <UserCircle className="w-4 h-4 text-zinc-600" />;
  };
  const badgeFor = (tr) => {
    if (tr === "admin") return "bg-red-200";
    if (tr === "manager") return "bg-orange-200";
    if (tr === "lead") return "bg-blue-200";
    return "bg-zinc-100";
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-3 text-xs font-mono flex-wrap">
        <span className="flex items-center gap-1"><Shield className="w-3.5 h-3.5 text-red-700" /> ADMIN</span>
        <span className="flex items-center gap-1"><Crown className="w-3.5 h-3.5 text-orange-700" /> MANAGER</span>
        <span className="flex items-center gap-1"><UsersIcon className="w-3.5 h-3.5 text-blue-700" /> LEAD</span>
        <span className="flex items-center gap-1"><UserCircle className="w-3.5 h-3.5 text-zinc-600" /> EMPLOYEE</span>
        <span className="ml-auto text-zinc-500">Scope: <b>{tree.scope}</b></span>
      </div>

      <div className="space-y-1.5">
        {flat.map((item) => {
          const n = item.node;
          const hasKids = (n.children || []).length > 0;
          const isOpen = expanded[n.user_id] !== false;
          const mgrId = (allUsers.find((u) => u.user_id === n.user_id) || {}).manager_id || "";
          const full = fullUser(n.user_id) || {};
          const isSuspended = full.is_active === false;
          return (
            <div key={n.user_id} className={"brut-card flex items-center gap-3 px-3 py-2.5 hover:bg-zinc-50 " + (isSuspended ? "opacity-60" : "")} style={{ marginLeft: item.depth * 22 }} data-testid={"tree-node-" + n.user_id}>
              <button onClick={() => toggle(n.user_id)} disabled={!hasKids} className={"p-1 " + (hasKids ? "" : "opacity-30")} aria-label="toggle">
                {hasKids && isOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
              </button>
              {iconFor(n.team_role)}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-bold truncate">{n.name}</span>
                  <span className={"font-mono text-[10px] uppercase px-1.5 py-0.5 border border-zinc-900 " + badgeFor(n.team_role)}>{n.team_role || "employee"}</span>
                  {n.team && <span className="font-mono text-[10px] uppercase px-1.5 py-0.5 bg-zinc-100 border border-zinc-900">{n.team}</span>}
                  {n.designation && <span className="font-mono text-[10px] px-1.5 py-0.5 bg-yellow-100 border border-zinc-900" title="Designation">{n.designation}</span>}
                  {n.department && <span className="font-mono text-[10px] px-1.5 py-0.5 bg-sky-100 border border-zinc-900" title="Department">{n.department}</span>}
                  <span className="font-mono text-[10px] text-zinc-500">{ROLE_LABELS[n.role] || n.role}</span>
                  {hasKids && <span className="font-mono text-[10px] text-zinc-500">- {n.children.length} reports</span>}
                  {isSuspended && <span className="font-mono text-[9px] uppercase px-1.5 py-0.5 border border-zinc-900 bg-red-200">SUSPENDED</span>}
                </div>
                <div className="font-mono text-[11px] text-zinc-500 truncate">{n.email} {n.employee_code ? "- " + n.employee_code : ""}</div>
              </div>
              {isAdmin && (
                <div className="flex items-center gap-2 flex-wrap justify-end">
                  <select className="brut-input px-1.5 py-1 text-[11px] font-mono" value={n.team_role || "employee"} onChange={(e) => setTeamRole(n.user_id, e.target.value)} data-testid={"tree-team-role-" + n.user_id}>
                    {TEAM_ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
                  </select>
                  <select className="brut-input px-1.5 py-1 text-[11px] font-mono max-w-[160px]" value={mgrId} onChange={(e) => setManager(n.user_id, e.target.value)} data-testid={"tree-manager-" + n.user_id}>
                    <option value="">- No Manager -</option>
                    {allUsers.filter((u) => u.user_id !== n.user_id).map((u) => <option key={u.user_id} value={u.user_id}>{u.name}</option>)}
                  </select>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => editName(full)}
                      className="brut-btn-secondary px-1.5 py-1 text-[9px] font-mono"
                      data-testid={`tree-edit-${n.user_id}`}
                      title="Edit display name"
                    >EDIT</button>
                    <button
                      onClick={() => resetToDefault(full)}
                      className="brut-btn-secondary px-1.5 py-1 text-[9px] font-mono"
                      data-testid={`tree-reset-pw-${n.user_id}`}
                      title="Reset password to onboarding default (LD@2026!)"
                    >RESET PW</button>
                    <button
                      onClick={() => emailCredentials(full)}
                      className="brut-btn-secondary px-1.5 py-1 text-[9px] font-mono bg-sky-100 hover:bg-sky-200"
                      data-testid={`tree-email-creds-${n.user_id}`}
                      title="Reset to a fresh random password and EMAIL this user their User ID + password"
                    >EMAIL CREDS</button>
                    <button
                      onClick={() => toggleActive(full)}
                      className={"brut-btn-secondary px-1.5 py-1 text-[9px] font-mono " + (isSuspended ? "bg-emerald-100 hover:bg-emerald-200" : "bg-amber-100 hover:bg-amber-200")}
                      data-testid={`tree-toggle-active-${n.user_id}`}
                      title={isSuspended ? "Reactivate this account" : "Suspend this account (locks login)"}
                    >{isSuspended ? "ACTIVATE" : "SUSPEND"}</button>
                    <button
                      onClick={() => hardDelete(full)}
                      className="brut-btn-secondary px-1.5 py-1 text-[9px] font-mono bg-red-100 hover:bg-red-200"
                      data-testid={`tree-delete-${n.user_id}`}
                      title="Permanently delete this user (irreversible)"
                    >DELETE</button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

